import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import AdminWrapper from "../../../components/AdminWrapper";
import { BASE_URL } from "../../../config/config";

// A tenant's review doesn't reach other prospective tenants until it's been
// looked at here — a single bad-faith review (from either side) shouldn't
// reach anyone unchecked.
export default function PropertyReviews() {
  const open = useSelector((state) => state.sidebar.open);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState({});
  const [decidingId, setDecidingId] = useState(null);

  const token = localStorage.getItem("token") || sessionStorage.getItem("ots_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/admin/property-reviews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.data || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load pending reviews");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, decision) => {
    setDecidingId(`${id}:${decision}`);
    try {
      const { data } = await axios.put(
        `${BASE_URL}/admin/property-reviews/${id}/decide`,
        { decision, note: notes[id] || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data?.message || "Review decided");
      setRows((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record decision");
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <>
      <AdminWrapper />
      <Box className={`main-container ${open ? "sidebar-open" : "sidebar-closed"}`} sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          Property Reviews
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Approve a review to make it public on the property's listing, along
          with the owner's response if they gave one.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">No reviews are waiting for moderation.</Alert>
        ) : (
          <Stack spacing={2}>
            {rows.map((r) => (
              <Paper key={r._id} elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box>
                    <Rating value={r.rating} readOnly size="small" />
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {r.comment || <em>No comment</em>}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {moment(r.createdAt).format("DD MMM YYYY")}
                  </Typography>
                </Stack>

                {r.ownerResponse?.message && (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    Owner's response: "{r.ownerResponse.message}"
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  size="small"
                  label="Note (internal, optional)"
                  value={notes[r._id] || ""}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [r._id]: e.target.value }))}
                  sx={{ mb: 1.5 }}
                />

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={Boolean(decidingId)}
                    onClick={() => decide(r._id, "APPROVE")}
                  >
                    {decidingId === `${r._id}:APPROVE` ? "Approving…" : "Approve — make public"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={Boolean(decidingId)}
                    onClick={() => decide(r._id, "REJECT")}
                  >
                    {decidingId === `${r._id}:REJECT` ? "Rejecting…" : "Reject"}
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </>
  );
}
